/**
 * EcommerceFooter - Pied de page legal : liens juridiques, coordonnees, TVA.
 *
 * Present sur TOUTES les pages du site (via ShopFooterLayout pour les routes
 * /shop/*, et directement dans la zone de defilement du catalogue) : un
 * controleur de la Region wallonne l'a demande pour l'autre site de la societe,
 * la meme exigence vaut ici.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import NewsletterSignup from '../NewsletterSignup';
import { fetchShopConfigCached } from '../../../hooks/ecommerce/useShopOrdering';
import { formatPlageHoraire } from '../../../utils/shopOrdering';
import type { PlageHoraire } from '../../../api/ecommerce.api';
import { logger } from '../../../utils/logger';
import { useTenant } from '../../../contexts/TenantContext';

export default function EcommerceFooter() {
  // L'identifiant du magasin voyage DANS l'URL du téléchargement : un lien cliqué
  // n'emporte pas l'en-tête `x-tenant-id` que l'application ajoute à ses appels.
  const { tenant } = useTenant();

  /**
   * Les heures d'ouverture. Les CGV, l'email « commande prête » et la fiche
   * produit renvoyaient tous « aux heures d'ouverture » — qui ne figuraient sur
   * aucune page du site. Le dimanche n'est pas au magasin mais au marché de
   * Mons : la plage porte son lieu, sinon un client vient à Elouges un jour de
   * marché. Réglées dans les paramètres du magasin, servies par /shop-config.
   */
  const [horaires, setHoraires] = useState<PlageHoraire[]>([]);
  useEffect(() => {
    fetchShopConfigCached()
      .then((c) => setHoraires(c.store?.opening_hours || []))
      .catch((err) => logger.error('Horaires du magasin indisponibles:', err));
  }, []);

  return (
    <footer className="bg-gray-100 border-t border-gray-200 py-8 mt-auto">
      <div className="max-w-6xl mx-auto px-6">
        {/* Actualités par email */}
        <NewsletterSignup source="footer" variant="footer" />

        {/* Liens juridiques */}
        <div className="flex flex-wrap justify-center gap-4 md:gap-8 mb-6">
          <Link
            to="/shop/cgv"
            className="text-sm text-gray-600 hover:text-green-700 transition-colors"
          >
            Conditions Generales de Vente
          </Link>
          <Link
            to="/shop/privacy"
            className="text-sm text-gray-600 hover:text-green-700 transition-colors"
          >
            Politique de confidentialite
          </Link>
          <Link
            to="/shop/cookies"
            className="text-sm text-gray-600 hover:text-green-700 transition-colors"
          >
            Cookies
          </Link>
          <Link
            to="/shop/mentions-legales"
            className="text-sm text-gray-600 hover:text-green-700 transition-colors"
          >
            Mentions legales
          </Link>
        </div>

        {/* Quand venir — et OU, le dimanche */}
        {horaires.length > 0 && (
          <div className="text-center text-sm text-gray-600 mb-6">
            <p className="font-medium text-gray-800 mb-2">Horaires</p>
            <ul className="space-y-0.5">
              {horaires.map((h) => (
                <li key={`${h.day}-${h.from}`} className="capitalize">
                  {formatPlageHoraire(h)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Coordonnees */}
        <div className="text-center text-sm text-gray-600 space-y-1">
          {/* Le nom de la société ramène à l'accueil : c'est ce qu'on attend d'un
              pied de page, et il n'était pas cliquable (signalé le 10/09/2026). */}
          <p className="font-medium text-gray-700">
            <Link to="/shop" className="hover:underline">
              Les Folies Maraicheres SRL
            </Link>
          </p>
          <p>Rue du Commerce 147 — 7370 Dour</p>
          <p>
            Tel. : 0478/51.68.22 — Email :{' '}
            <a href="mailto:contact@foliesboutik.be" className="text-green-700 hover:underline">
              contact@foliesboutik.be
            </a>
          </p>
          <p>
            T.V.A. 0715954426 — RPM Mons
          </p>

          {/* ACCESSIBILITÉ — demande d'une cliente qui perd la vue (09/2026).
              Un lien ORDINAIRE, dans le pied de page qui est sur toutes les
              pages de la boutique : c'est là qu'on cherche ce genre de chose,
              et c'est atteignable au clavier comme au lecteur d'écran. Le
              libellé dit le format ET l'usage — « télécharger » seul ne permet
              pas de décider si ça vaut la peine de cliquer.
              Pas de `download` : le fichier porte déjà son
              Content-Disposition côté serveur, et un attribut `download` sur
              une URL d'API est ignoré hors même origine. */}
          <p className="pt-2">
            <a
              href={`${import.meta.env.VITE_API_URL || '/api'}/shop-config/liste-produits.txt${
                tenant?.id ? `?t=${tenant.id}` : ''
              }`}
              className="text-green-700 underline hover:text-green-800"
            >
              Télécharger la liste de nos produits et prix (fichier texte, lisible
              par lecteur d'écran)
            </a>
          </p>
          <p>
            Certifie bio par <strong className="font-medium">Certisys</strong> — controle BE-BIO-01,
            mention Nature &amp; Progres
          </p>
          <p className="pt-2 text-gray-600">
            &copy; {new Date().getFullYear()} Les Folies Maraicheres SRL — Tous droits reserves
          </p>
        </div>
      </div>
    </footer>
  );
}
